/**
 * SeuilsEchelonnementWidget — Widget compact pour configurer les seuils
 * de paiement échelonné depuis le dashboard Super Admin.
 *
 * Stockés dans MongoDB `app_config` (clé `seuils_echelonnement`).
 *
 * V36.11 — 28/05/2026.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, RotateCcw, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { getAuthHeader } from '../../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LABELS = {
  default: 'RCDS Annuel',
  nominative: 'RCDS Nominative',
  domium: 'DOS DOMIUM',
};

const HINTS = {
  default: 'Seuil à partir duquel le paiement échelonné est proposé pour RCDS annuel.',
  nominative: 'RCDS par chantier — seuil typiquement plus bas (acompte+SEPA dès 1 000 €).',
  domium: 'DOS DOMIUM — actif pour les souscriptions multirisques.',
};

const fmtEur = (v) => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function SeuilsEchelonnementWidget() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seuils, setSeuils] = useState({ default: 2500, nominative: 1000, domium: 2500 });
  const [defaults, setDefaults] = useState({ default: 2500, nominative: 1000, domium: 2500 });
  const [isCustom, setIsCustom] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchSeuils = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/seuils-echelonnement`, { headers: getAuthHeader() });
      setSeuils(res.data?.seuils || {});
      setDefaults(res.data?.defaults || {});
      setIsCustom(!!res.data?.is_customized);
      setDirty(false);
    } catch {
      toast.error('Impossible de charger les seuils');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSeuils(); }, []);

  const handleChange = (key, val) => {
    const num = parseFloat(val);
    if (Number.isFinite(num) && num >= 0) {
      setSeuils({ ...seuils, [key]: num });
      setDirty(true);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seuils-echelonnement`, seuils, { headers: getAuthHeader() });
      toast.success('Seuils enregistrés. Effet immédiat sur tous les nouveaux PDF.');
      await fetchSeuils();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser tous les seuils aux valeurs par défaut métier ?')) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/seuils-echelonnement/reset`, {}, { headers: getAuthHeader() });
      toast.success('Seuils réinitialisés');
      await fetchSeuils();
    } catch {
      toast.error('Erreur lors du reset');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement des seuils…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="seuils-echelonnement-widget">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              💳 Seuils Paiement échelonné
              {isCustom && (
                <span className="text-[10px] uppercase font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                  Personnalisé
                </span>
              )}
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Modification immédiate sur tous les PDF + simulateurs. Cache 60 s.
            </CardDescription>
          </div>
          <Button
            variant="ghost" size="sm"
            onClick={handleReset}
            disabled={saving || !isCustom}
            className="text-slate-500 hover:text-slate-700"
            data-testid="seuils-reset-btn"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Réinitialiser
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.keys(LABELS).map((key) => (
            <div key={key} className="rounded-lg border border-slate-200 p-3 bg-slate-50/40">
              <Label htmlFor={`seuil-${key}`} className="text-xs font-semibold text-slate-700">
                {LABELS[key]}
              </Label>
              <div className="mt-1 flex items-center gap-1">
                <Input
                  id={`seuil-${key}`}
                  type="number" step="1" min="0"
                  value={seuils[key] || 0}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="h-9 text-sm font-mono"
                  data-testid={`seuil-input-${key}`}
                />
                <span className="text-sm font-semibold text-slate-600">€</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5 leading-tight">{HINTS[key]}</p>
              {seuils[key] !== defaults[key] && (
                <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Défaut métier : {fmtEur(defaults[key])} €
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="seuils-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
